// src/components/books/InlineVideoPlayer.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './InlineVideoPlayer.css';

export default function InlineVideoPlayer({ src }) {
  const videoRef = useRef(null);
  const wrapperRef = useRef(null);
  const progressBarRef = useRef(null);
  const hideTimerRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [played, setPlayed] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [showRateMenu, setShowRateMenu] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    setIsPlaying(false);
    setPlayed(0);
    setBuffered(0);
    setDuration(0);
    setControlsVisible(true);
    setShowRateMenu(false);
  }, [src]);

  const resetHideTimer = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (isPlaying) {
      hideTimerRef.current = setTimeout(() => setControlsVisible(false), 1500);
    }
  }, [isPlaying]);

  useEffect(() => {
    if (controlsVisible && isPlaying) resetHideTimer();
    else if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, [controlsVisible, isPlaying, resetHideTimer]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) videoRef.current.play();
    else videoRef.current.pause();
  };

  const handleVideoClick = () => {
    setControlsVisible((prev) => !prev);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) setPlayed(videoRef.current.currentTime);
  };

  const handleProgress = () => {
    if (videoRef.current && videoRef.current.buffered.length > 0) {
      setBuffered(videoRef.current.buffered.end(videoRef.current.buffered.length - 1));
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  };

  const handleProgressBarClick = (e) => {
    if (!videoRef.current || !progressBarRef.current || !duration) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = pos * duration;
    resetHideTimer();
  };

  const handleVolumeChange = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (videoRef.current) videoRef.current.volume = v;
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    const next = !muted;
    setMuted(next);
    videoRef.current.muted = next;
  };

  const setRate = (rate) => {
    setPlaybackRate(rate);
    if (videoRef.current) videoRef.current.playbackRate = rate;
    setShowRateMenu(false);
  };

  const toggleFullscreen = () => {
    const el = wrapperRef.current;
    if (!document.fullscreenElement) {
      el?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  const skip = (s) => {
    if (videoRef.current) videoRef.current.currentTime += s;
    resetHideTimer();
  };

  const fmt = (secs) => {
    if (!isFinite(secs) || isNaN(secs)) return '00:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  return (
    <div className="ivp-root" ref={wrapperRef}>
      <video
        ref={videoRef}
        src={src}
        className="ivp-video"
        onClick={handleVideoClick}
        onPlay={() => { setIsPlaying(true); setControlsVisible(true); }}
        onPause={() => { setIsPlaying(false); setControlsVisible(true); }}
        onTimeUpdate={handleTimeUpdate}
        onProgress={handleProgress}
        onLoadedMetadata={handleLoadedMetadata}
        onContextMenu={(e) => e.preventDefault()}
        controlsList="nodownload noplaybackrate noremoteplayback"
        disablePictureInPicture
        playsInline
      />

      {!isPlaying && played === 0 && (
        <button className="ivp-center-play" onClick={togglePlay} aria-label="پخش">
          <svg viewBox="0 0 24 24" width="40" height="40">
            <path d="M8 5v14l11-7z" fill="currentColor" />
          </svg>
        </button>
      )}

      <div
        className={`ivp-controls ${controlsVisible ? 'is-visible' : 'is-hidden'}`}
        onMouseMove={resetHideTimer}
      >
        <div className="ivp-progress-row">
          <div
            className="ivp-progress-bar-container"
            ref={progressBarRef}
            onClick={handleProgressBarClick}
          >
            <div
              className="ivp-progress-buffered"
              style={{ width: `${duration > 0 ? (buffered / duration) * 100 : 0}%` }}
            />
            <div
              className="ivp-progress-played"
              style={{ width: `${duration > 0 ? (played / duration) * 100 : 0}%` }}
            >
              <div className="ivp-progress-thumb" />
            </div>
          </div>
          <div className="ivp-time">
            <span>{fmt(played)}</span>
            <span>/</span>
            <span>{fmt(duration)}</span>
          </div>
        </div>

        <div className="ivp-controls-row">
          <button className="ivp-ctrl" onClick={() => skip(-10)} title="۱۰ ثانیه عقب">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z" />
            </svg>
          </button>
          <button className="ivp-ctrl ivp-ctrl-play" onClick={togglePlay}>
            {isPlaying ? (
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
          <button className="ivp-ctrl" onClick={() => skip(10)} title="۱۰ ثانیه جلو">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M11.5 8c2.65 0 5.05.99 6.9 2.6L22 7v9h-9l3.62-3.62c-1.39-1.16-3.16-1.88-5.12-1.88-3.54 0-6.55 2.31-7.6 5.5l-2.37-.78C2.92 11.03 6.85 8 11.5 8z" />
            </svg>
          </button>

          <div className="ivp-rate-wrap">
            <button
              className="ivp-ctrl ivp-rate-btn"
              onClick={() => setShowRateMenu((p) => !p)}
            >
              {playbackRate}x
            </button>
            {showRateMenu && (
              <div className="ivp-rate-menu">
                {[0.5, 0.75, 1, 1.25, 1.5, 2].map((r) => (
                  <button
                    key={r}
                    className={playbackRate === r ? 'active' : ''}
                    onClick={() => setRate(r)}
                  >
                    {r}x
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="ivp-vol-wrap">
            <button className="ivp-ctrl" onClick={toggleMute}>
              {muted || volume === 0 ? (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                </svg>
              )}
            </button>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={muted ? 0 : volume}
              onChange={handleVolumeChange}
              className="ivp-vol"
            />
          </div>

          <button className="ivp-ctrl" onClick={toggleFullscreen} title="تمام صفحه">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}